import { AuthApiClient } from './core/auth-client';
import { OrdersApiClient } from './core/orders-client';
import { ProductsApiClient } from './core/products-client';
import { BillingApiClient } from './core/billing-client';
import { ServicesApiClient } from './core/services-client';
import { PaymentsApiClient } from './core/payments-client';
import { VPNApiClient } from './core/vpn-client';
import { SambaApiClient } from './core/samba-client';
import { NFSApiClient } from './core/nfs-client';
import { UserGroupsApiClient } from './core/user-groups-client';
import { ActivityApiClient } from './core/activity-client';
import { SettingsApiClient } from './core/settings-client';
import { SharesApiClient } from './core/shares-client';
import { FTPApiClient } from './core/ftp-client';
import { BackupsApiClient } from './core/backups-client';
import { SubusersApiClient } from './core/subusers-client';
import { LinuxUserApiClient } from './core/linux-user-client';
import { LDAPClient } from './core/ldap-client';
import { MonitoringAPIClient } from './core/monitoring-client';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

export class ApiClient {
  private static instance: ApiClient;
  
  readonly auth: AuthApiClient;
  readonly orders: OrdersApiClient;
  readonly products: ProductsApiClient;
  readonly billing: BillingApiClient;
  readonly services: ServicesApiClient;
  readonly payments: PaymentsApiClient;
  readonly vpn: VPNApiClient;
  readonly samba: SambaApiClient;
  readonly nfs: NFSApiClient;
  readonly userGroups: UserGroupsApiClient;
  readonly activity: ActivityApiClient;
  readonly settings: SettingsApiClient;
  readonly shares: SharesApiClient;
  readonly ftp: FTPApiClient;
  readonly backups: BackupsApiClient;
  readonly subusers: SubusersApiClient;
  readonly linuxUser: LinuxUserApiClient;
  readonly ldap: LDAPClient;
  readonly monitoring: MonitoringAPIClient;

  // Proxy methods for backward compatibility
  readonly getAuthToken: () => string | null;
  readonly setAuthToken: (token: string) => Promise<void>;
  readonly clearAuthToken: () => Promise<void>;
  readonly login: AuthApiClient['login'];
  readonly register: AuthApiClient['register'];
  readonly getCurrentUser: AuthApiClient['getCurrentUser'];
  readonly updateCurrentUser: AuthApiClient['updateCurrentUser'];
  readonly getUserSettings: AuthApiClient['getUserSettings'];
  readonly updateUserSettings: AuthApiClient['updateUserSettings'];
  readonly checkPendingPlan: AuthApiClient['checkPendingPlan'];
  readonly detectCountry: AuthApiClient['detectCountry'];
  readonly requestPasswordReset: AuthApiClient['requestPasswordReset'];
  readonly resetPassword: AuthApiClient['resetPassword'];
  readonly convertToBusinessAccount: AuthApiClient['convertToBusinessAccount'];

  readonly createOrder: OrdersApiClient['createOrder'];
  readonly checkOrderStatus: OrdersApiClient['checkOrderStatus'];
  readonly extendOrder: OrdersApiClient['extendOrder'];
  readonly retryPayment: OrdersApiClient['retryPayment'];
  readonly getPaymentIntent: OrdersApiClient['getPaymentIntent'];
  readonly calculateVAT: OrdersApiClient['calculateVAT'];
  readonly calculatePrice: OrdersApiClient['calculatePrice'];

  readonly getProducts: ProductsApiClient['getProducts'];
  readonly getProduct: ProductsApiClient['getProduct'];
  readonly getService: ProductsApiClient['getService'];
  readonly getDashboardData: ProductsApiClient['getDashboardData'];
  readonly getProductConfig: ProductsApiClient['getProductConfig'];
  readonly updateService: ProductsApiClient['updateService'];

  readonly getBillingOverview: BillingApiClient['getBillingOverview'];
  readonly getInvoices: BillingApiClient['getInvoices'];
  readonly getInvoice: BillingApiClient['getInvoice'];
  readonly getInvoiceData: BillingApiClient['getInvoiceData'];
  readonly downloadInvoicePdf: BillingApiClient['downloadInvoicePdf'];
  readonly downloadInvoice: BillingApiClient['downloadInvoice'];
  readonly getInvoicePreview: BillingApiClient['getInvoicePreview'];
  readonly updateBillingInfo: BillingApiClient['updateBillingInfo'];

  readonly getActivity: ActivityApiClient['getActivity'];
  readonly getServiceAuditLogs: ActivityApiClient['getServiceAuditLogs'];

  private constructor() {
    // Initialize all client modules
    this.auth = new AuthApiClient();
    this.orders = new OrdersApiClient();
    this.products = new ProductsApiClient();
    this.billing = new BillingApiClient();
    this.services = new ServicesApiClient();
    this.payments = new PaymentsApiClient();
    this.vpn = new VPNApiClient();
    this.samba = new SambaApiClient();
    this.nfs = new NFSApiClient();
    this.userGroups = new UserGroupsApiClient();
    this.activity = new ActivityApiClient();
    this.settings = new SettingsApiClient();
    this.shares = new SharesApiClient();
    this.ftp = new FTPApiClient();
    this.backups = new BackupsApiClient();
    this.subusers = new SubusersApiClient();
    this.linuxUser = new LinuxUserApiClient();
    this.ldap = new LDAPClient();
    this.monitoring = new MonitoringAPIClient();

    // Set up WebSocket token management
    this.auth.onTokenSet((token) => {
      // Any WebSocket reconnection logic can go here
    });

    this.auth.onTokenClear(() => {
      // Any WebSocket cleanup logic can go here
    });

    // Bind auth methods
    this.getAuthToken = this.auth.getAuthToken.bind(this.auth);
    this.setAuthToken = this.auth.setAuthToken.bind(this.auth);
    this.clearAuthToken = this.auth.clearAuthToken.bind(this.auth);
    this.login = this.auth.login.bind(this.auth);
    this.register = this.auth.register.bind(this.auth);
    this.getCurrentUser = this.auth.getCurrentUser.bind(this.auth);
    this.updateCurrentUser = this.auth.updateCurrentUser.bind(this.auth);
    this.getUserSettings = this.auth.getUserSettings.bind(this.auth);
    this.updateUserSettings = this.auth.updateUserSettings.bind(this.auth);
    this.checkPendingPlan = this.auth.checkPendingPlan.bind(this.auth);
    this.detectCountry = this.auth.detectCountry.bind(this.auth);
    this.requestPasswordReset = this.auth.requestPasswordReset.bind(this.auth);
    this.resetPassword = this.auth.resetPassword.bind(this.auth);
    this.convertToBusinessAccount = this.auth.convertToBusinessAccount.bind(this.auth);

    // Bind order methods
    this.createOrder = this.orders.createOrder.bind(this.orders);
    this.checkOrderStatus = this.orders.checkOrderStatus.bind(this.orders);
    this.extendOrder = this.orders.extendOrder.bind(this.orders);
    this.retryPayment = this.orders.retryPayment.bind(this.orders);
    this.getPaymentIntent = this.orders.getPaymentIntent.bind(this.orders);
    this.calculateVAT = this.orders.calculateVAT.bind(this.orders);
    this.calculatePrice = this.orders.calculatePrice.bind(this.orders);

    // Bind product methods
    this.getProducts = this.products.getProducts.bind(this.products);
    this.getProduct = this.products.getProduct.bind(this.products);
    this.getService = this.products.getService.bind(this.products);
    this.getDashboardData = this.products.getDashboardData.bind(this.products);
    this.getProductConfig = this.products.getProductConfig.bind(this.products);
    this.updateService = this.products.updateService.bind(this.products);

    // Bind billing methods
    this.getBillingOverview = this.billing.getBillingOverview.bind(this.billing);
    this.getInvoices = this.billing.getInvoices.bind(this.billing);
    this.getInvoice = this.billing.getInvoice.bind(this.billing);
    this.getInvoiceData = this.billing.getInvoiceData.bind(this.billing);
    this.downloadInvoicePdf = this.billing.downloadInvoicePdf.bind(this.billing);
    this.downloadInvoice = this.billing.downloadInvoice.bind(this.billing);
    this.getInvoicePreview = this.billing.getInvoicePreview.bind(this.billing);
    this.updateBillingInfo = this.billing.updateBillingInfo.bind(this.billing);

    // Bind activity methods
    this.getActivity = this.activity.getActivity.bind(this.activity);
    this.getServiceAuditLogs = this.activity.getServiceAuditLogs.bind(this.activity);
  }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  // Proxy axios instance
  get axios(): AxiosInstance {
    return this.auth.axios;
  }

  // HTTP methods for backward compatibility
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.delete<T>(url, config);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();

// Re-export types from all modules
export * from './types/auth';
export * from './types/orders';
export * from './types/products';
export * from './types/billing';
export * from './types/payments';
export * from './types/activity';
export * from './types/settings';
